import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { AlertCircle, ArrowDown, ArrowUp, ArrowUpDown, Check, Loader2, X } from 'lucide-react';
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ModuleTile, useCurrentModule, useCurrentSection } from '@/components/brand';
import { useLocale, useT, useTranslator, type EnumKind } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { DateTimeInput, type DateTimeKind } from './datetime';
import { ColorInput, FileInput, NumberInput, PasswordInput, SearchInput, TextareaInput, TextInput, type TextInputProps } from './inputs';

export { Checkbox, CheckboxField, Radio, RadioCards, RadioField, SegmentedControl, type ChoiceOption } from './choice';
export { DateTimeInput } from './datetime';
export { ColorInput, FileInput, NumberInput, PasswordInput, SearchInput, TextInput, type TextInputProps } from './inputs';
export { Popover, PopoverAnchor, PopoverClose, PopoverPanel, PopoverTrigger } from './popover';
export { Select, type SelectProps } from './select';
export { controlClass } from './styles';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'brand' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-action text-action-foreground shadow-[inset_0_0_0_2px_var(--action-edge,transparent)] hover:bg-action-hover focus-visible:ring-slate-400',
  secondary: 'bg-white text-ink border border-line hover:bg-slate-50 focus-visible:ring-slate-300',
  outline: 'bg-transparent border border-current hover:bg-white/10 focus-visible:ring-current',
  ghost: 'bg-transparent text-ink hover:bg-slate-100 focus-visible:ring-slate-300',
  danger: 'bg-danger text-white hover:bg-red-700 focus-visible:ring-red-300',
  brand: 'bg-brand text-brand-foreground hover:brightness-95 focus-visible:ring-brand',
};
const buttonSizes: Record<ButtonSize, string> = { sm: 'h-8 px-3 text-sm', md: 'h-10 px-4 text-sm', lg: 'h-12 px-6 text-base' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ className, variant = 'primary', size = 'md', loading, children, disabled, ...props }, ref) {
  return (
    <button
      ref={ref}
      className={cn('press inline-flex items-center justify-center gap-2 rounded-[var(--radius-button,var(--radius-control))] font-semibold transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50', buttonVariants[variant], buttonSizes[size], className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

const DATE_KINDS: readonly string[] = ['date', 'datetime-local', 'time'];
const isDateKind = (type: string | undefined): type is DateTimeKind => DATE_KINDS.includes(type ?? '');

/**
 * One entry point for every single-line field. The type decides which control
 * Bulk draws: the browser's own widget is only ever used where it already looks
 * like ours (text, email, phone), and never where it opens its own window.
 */
export const Input = forwardRef<HTMLInputElement, TextInputProps>(function Input({ type, ...props }, ref) {
  if (isDateKind(type)) return <DateTimeInput ref={ref} type={type} {...props} />;
  if (type === 'number') return <NumberInput ref={ref} {...props} />;
  if (type === 'color') return <ColorInput ref={ref} {...props} />;
  if (type === 'password') return <PasswordInput ref={ref} {...props} />;
  if (type === 'search') return <SearchInput ref={ref} {...props} />;
  if (type === 'file') return <FileInput ref={ref} {...props} />;
  return <TextInput ref={ref} type={type} {...props} />;
});

export const Textarea = TextareaInput;

export function Label({ children, htmlFor, className }: { children: ReactNode; htmlFor?: string; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn('mb-1 block text-sm font-medium text-ink', className)}>
      {children}
    </label>
  );
}

export function Field({ label, error, hint, children, htmlFor }: { label: string; error?: string | undefined; hint?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

export function Card({ children, className, title, description, actions }: { children?: ReactNode; className?: string; title?: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <section className={cn('min-w-0 rounded-[var(--radius-card)] border border-line bg-white', className)}>
      {title || actions ? (
        <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3.5 sm:px-5 sm:py-4">
          <div className="min-w-0">
            {title ? <h2 className="text-base font-bold tracking-[-0.01em] text-ink">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

const badgeTones: Record<string, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  brand: 'bg-brand text-brand-foreground',
};

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: keyof typeof badgeTones; className?: string }) {
  return <span data-badge className={cn('inline-flex items-center whitespace-nowrap rounded-[6px] px-2 py-0.5 text-xs font-semibold', badgeTones[tone], className)}>{children}</span>;
}

export function statusTone(status: string): keyof typeof badgeTones {
  switch (status) {
    case 'ACTIVE':
    case 'PAID':
    case 'CONNECTED':
    case 'GRANTED':
    case 'BOOKED':
    case 'ATTENDED':
    case 'VERIFIED':
    case 'COMPLETED':
    case 'SENT':
    case 'DONE':
      return 'success';
    case 'PENDING':
    case 'PAUSED':
    case 'WAITLISTED':
    case 'DEGRADED':
    case 'REQUESTED':
    case 'QUEUED':
    case 'AUTHORIZED':
    case 'DRAFT':
    case 'SCHEDULED':
      return 'warning';
    case 'EXPIRED':
    case 'CANCELLED':
    case 'FAILED':
    case 'DENIED':
    case 'SUSPENDED':
    case 'BLOCKED':
    case 'ERROR':
    case 'INVALID_CREDENTIALS':
    case 'DISCONNECTED':
    case 'NO_SHOW':
    case 'DEAD':
      return 'danger';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
    case 'READ':
      return 'info';
    default:
      return 'neutral';
  }
}

export function StatusBadge({ status, label, kind = 'status' }: { status: string; label?: string; kind?: EnumKind }) {
  const { enumLabel } = useTranslator();
  return <Badge tone={statusTone(status)}>{label ?? enumLabel(kind, status)}</Badge>;
}

export function Spinner({ className }: { className?: string }) {
  const t = useT();
  return <Loader2 className={cn('h-5 w-5 animate-spin text-ink-muted', className)} aria-label={t('common.loadingLabel')} />;
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-card)] border border-dashed border-line px-6 py-12 text-center">
      {icon ? <div className="mb-3 text-ink-muted">{icon}</div> : null}
      <p className="text-base font-medium text-ink">{title}</p>
      {description ? <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex items-start gap-3 rounded-[var(--radius-card)] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p>{message}</p>
        {onRetry ? (
          <button className="mt-2 text-xs font-medium underline" onClick={onRetry}>
            {t('common.retry')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  const module = useCurrentModule();
  const section = useCurrentSection();
  const locale = useLocale();
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 lg:mb-8">
      <div className="flex min-w-0 items-start gap-3.5">
        {module ? <ModuleTile module={module.key} icon={section?.icon} size="lg" className="mt-0.5 hidden sm:inline-flex" /> : null}
        <div className="min-w-0">
          {module ? (
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-ink-muted">
              <ModuleTile module={module.key} icon={section?.icon} size="xs" className="sm:hidden" />
              {section?.label ?? module.name[locale]}
            </p>
          ) : null}
          <h1 className="break-words text-2xl font-extrabold tracking-[-0.03em] text-ink sm:text-[1.75rem] sm:leading-9">{title}</h1>
          {description ? <p className="mt-1 max-w-2xl text-sm text-ink-muted">{description}</p> : null}
        </div>
      </div>
      {actions ? <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'success' | 'danger' | 'warning' }) {
  return (
    <div className="min-w-0 rounded-[var(--radius-card)] border border-line bg-white px-4 py-3.5 sm:px-5 sm:py-4">
      <p className="text-[13px] font-medium leading-snug text-ink-muted">{label}</p>
      <p className={cn('mt-1 break-words text-2xl font-extrabold tracking-[-0.03em] text-ink', tone === 'success' && 'text-success', tone === 'danger' && 'text-danger', tone === 'warning' && 'text-warning')}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('scrollbar-thin overflow-x-auto rounded-[var(--radius-card)] border border-line bg-white', className)}>
      <table className="w-full min-w-[640px] text-left text-sm">{children}</table>
    </div>
  );
}

const HEAD_CELL = 'border-b border-line bg-slate-50 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted';

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return <th className={cn(HEAD_CELL, className)}>{children}</th>;
}

export type SortOrder = 'asc' | 'desc';
export interface Sorting<K extends string = string> {
  sort: K;
  order: SortOrder;
}

/**
 * A column header that sorts its table. The first click on a column sorts it in that column's
 * natural direction (`first`: A→Z for words, newest first for dates); a click on the sorted
 * column reverses it.
 */
export function SortableHead<K extends string>({ column, sorting, onSort, first = 'asc', label, className, children }: { column: K; sorting: Sorting<K>; onSort: (next: Sorting<K>) => void; first?: SortOrder; label?: string; className?: string; children: ReactNode }) {
  const active = sorting.sort === column;
  const Arrow = !active ? ArrowUpDown : sorting.order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th aria-sort={active ? (sorting.order === 'asc' ? 'ascending' : 'descending') : undefined} className={cn(HEAD_CELL, className)}>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => onSort({ sort: column, order: active ? (sorting.order === 'asc' ? 'desc' : 'asc') : first })}
        className={cn('group -mx-1 inline-flex items-center gap-1 rounded-[4px] px-1 py-0.5 uppercase tracking-wider transition hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300', active && 'text-ink')}
      >
        {children}
        <Arrow aria-hidden className={cn('h-3 w-3 shrink-0 transition-opacity', !active && 'opacity-40 group-hover:opacity-100 group-focus-visible:opacity-100')} />
      </button>
    </th>
  );
}

export function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return (
    <td className={cn('border-b border-line px-4 py-3 align-middle text-ink', className)} colSpan={colSpan}>
      {children}
    </td>
  );
}

export function Pagination({ page, pageSize, total, onChange }: { page: number; pageSize: number; total: number; onChange: (page: number) => void }) {
  const t = useT();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-ink-muted">
      <span>{t('common.pagination', { page, pages, total })}</span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          {t('common.previous')}
        </Button>
        <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}

function useScrollEdges() {
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ above: false, below: false });
  useEffect(() => {
    if (!body) return;
    const measure = () => {
      const above = body.scrollTop > 0;
      const below = body.scrollTop + body.clientHeight < body.scrollHeight - 1;
      setEdges((current) => (current.above === above && current.below === below ? current : { above, below }));
    };
    measure();
    body.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    if (body.firstElementChild) observer.observe(body.firstElementChild);
    return () => {
      body.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [body]);
  return { ref: setBody, ...edges };
}

export function Dialog({ open, onOpenChange, title, description, children, footer, wide }: { open: boolean; onOpenChange: (open: boolean) => void; title: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; wide?: boolean | 'xl' }) {
  const t = useT();
  const body = useScrollEdges();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]" />
        <DialogPrimitive.Content className={cn('fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[var(--radius-card)] bg-white shadow-xl focus:outline-none', wide === 'xl' ? 'max-w-6xl' : wide ? 'max-w-3xl' : 'max-w-lg')}>
          <div className={cn('flex shrink-0 items-start justify-between gap-4 border-b px-5 pb-3 pt-5 transition-colors sm:px-6 sm:pt-6', body.above ? 'border-line' : 'border-transparent')}>
            <div className="min-w-0">
              <DialogPrimitive.Title className="break-words text-lg font-bold tracking-[-0.02em] text-ink">{title}</DialogPrimitive.Title>
              {description ? <DialogPrimitive.Description className="mt-1 text-sm text-ink-muted">{description}</DialogPrimitive.Description> : null}
            </div>
            <DialogPrimitive.Close className="shrink-0 rounded-md p-1 text-ink-muted hover:bg-slate-100" aria-label={t('common.close')}>
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          {children ? (
            <div ref={body.ref} className={cn('scrollbar-thin min-h-0 overflow-y-auto px-5 pt-1 sm:px-6', footer ? 'pb-2' : 'pb-5 sm:pb-6')}>
              <div>{children}</div>
            </div>
          ) : null}
          {footer ? <div className={cn('flex shrink-0 flex-wrap justify-end gap-2 border-t px-5 pb-5 pt-4 transition-colors sm:px-6 sm:pb-6', body.below ? 'border-line' : 'border-transparent')}>{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, danger, onConfirm, loading }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; description?: string; confirmLabel?: string; danger?: boolean; onConfirm: () => void | Promise<void>; loading?: boolean }) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={() => void onConfirm()}>
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    />
  );
}

export const Tabs = TabsPrimitive.Root;
export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  return <TabsPrimitive.List className={cn('mb-4 inline-flex max-w-full flex-wrap gap-1 rounded-[12px] bg-slate-100 p-1', className)}>{children}</TabsPrimitive.List>;
}
export function TabsTrigger({ value, children }: { value: string; children: ReactNode }) {
  return (
    <TabsPrimitive.Trigger value={value} className="press rounded-[8px] px-3 py-1.5 text-sm font-semibold text-ink-muted transition hover:bg-white/60 hover:text-ink data-[state=active]:bg-white data-[state=active]:text-ink data-[state=active]:shadow-sm">
      {children}
    </TabsPrimitive.Trigger>
  );
}
export function TabsContent({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <TabsPrimitive.Content value={value} className={cn('animate-panel-in', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}

export function Switch({ checked, onCheckedChange, id, disabled }: { checked: boolean; onCheckedChange: (v: boolean) => void; id?: string; disabled?: boolean }) {
  return (
    <SwitchPrimitive.Root id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} className="relative h-6 w-11 shrink-0 rounded-full bg-slate-300 transition data-[state=checked]:bg-ink disabled:opacity-50">
      <SwitchPrimitive.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow transition data-[state=checked]:translate-x-[22px]" />
    </SwitchPrimitive.Root>
  );
}

interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: 'success' | 'error' | 'info';
}
interface ToastState {
  toast: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (err: unknown, title?: string) => void;
}
const ToastContext = createContext<ToastState | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), t.tone === 'error' ? 7000 : 4000);
  }, []);
  const value = useMemo<ToastState>(
    () => ({
      toast,
      success: (title, description) => toast({ title, description, tone: 'success' }),
      error: (err, title) => toast({ title: title ?? t('common.error'), description: err instanceof Error ? err.message : String(err), tone: 'error' }),
    }),
    [toast, t],
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(92vw,360px)] flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className={cn('pointer-events-auto animate-fade-in rounded-[12px] border px-4 py-3 text-sm shadow-lg', t.tone === 'success' && 'border-green-200 bg-white text-green-900', t.tone === 'error' && 'border-red-200 bg-white text-red-900', t.tone === 'info' && 'border-line bg-white text-ink')}>
            <div className="flex items-start gap-2">
              {t.tone === 'success' ? <Check className="mt-0.5 h-4 w-4 text-success" /> : t.tone === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 text-danger" /> : null}
              <div>
                <p className="font-medium">{t.title}</p>
                {t.description ? <p className="mt-0.5 text-xs opacity-80">{t.description}</p> : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastState {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
